/**
 * EmptyState — designed empty states. INTERACTION_SPEC §quality bar:
 * "Empty states are designed states. Never blank."
 *
 * Four tones map to the design handoff (screen-empty-states.jsx):
 *   - celebrate (ok): inbox-zero, all-caught-up, queue clean — green tint
 *   - muted: filtered-no-matches, search-no-results — neutral ink
 *   - instruct (info): first-run, onboarding — info blue tint
 *   - warn: offline, 403, 404 — warn amber tint
 *
 * Layout: vertically-centered icon + heading + sub-text + action row.
 * Renders nothing if you pass nothing — but you should always pass at
 * least an icon, heading, and one action.
 *
 * Use this for surface-level empty states (inbox, history, queue).
 * For inline pane empty states (no time logged, no attachments) use
 * the `<EmptyDashed>` variant — same primitive, dashed-box treatment.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/cn.js';

export type EmptyStateTone = 'celebrate' | 'muted' | 'instruct' | 'warn';

interface EmptyStateProps {
  tone?: EmptyStateTone;
  icon: ReactNode;
  /** Headline. Sentence case, no trailing period. */
  heading: ReactNode;
  /** Supporting text. Optional but encouraged — explain WHY there's
   *  nothing here, not just THAT there's nothing. */
  sub?: ReactNode;
  /** Action buttons / links. Render a `<Button>` row here. */
  actions?: ReactNode;
  /** Foot note. Mono, dim — meta info like "next run · 1 May". */
  footnote?: ReactNode;
  /** Min height of the centred block (px). Default 160. */
  minHeight?: number;
  className?: string;
}

const TONE_BG: Record<EmptyStateTone, string> = {
  celebrate: 'bg-ok-500/15 text-ok-400',
  muted: 'bg-ink-700 text-ink-300',
  instruct: 'bg-info-500/15 text-info-400',
  warn: 'bg-warn-500/15 text-warn-400',
};

export function EmptyState({
  tone = 'muted',
  icon,
  heading,
  sub,
  actions,
  footnote,
  minHeight = 160,
  className,
}: EmptyStateProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center justify-center text-center px-6 py-8 fade-in-160',
        className
      )}
      style={{ minHeight }}
    >
      <div
        className={cn(
          'w-12 h-12 rounded-full inline-flex items-center justify-center',
          TONE_BG[tone]
        )}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div className="text-[14px] font-semibold text-ink-100 mt-3">
        {heading}
      </div>
      {sub && (
        <div className="text-[12.5px] text-ink-300 mt-1 max-w-[320px] leading-relaxed">
          {sub}
        </div>
      )}
      {actions && <div className="mt-4 flex items-center gap-2">{actions}</div>}
      {footnote && (
        <div className="mt-3 text-[11px] text-ink-400 mono">{footnote}</div>
      )}
    </div>
  );
}

/**
 * Inline pane empty state — dashed box treatment for "this section
 * has no items" inside a side panel. Tighter than EmptyState.
 */
interface EmptyDashedProps {
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}

export function EmptyDashed({ icon, children, className }: EmptyDashedProps) {
  return (
    <div
      className={cn(
        'bg-ink-800 border border-ink-600 border-dashed rounded-md p-3 flex items-center gap-2 fade-in-160',
        className
      )}
    >
      <span className="text-ink-300 shrink-0" aria-hidden="true">
        {icon}
      </span>
      <span className="text-[12px] text-ink-300">{children}</span>
    </div>
  );
}
